import { computed, ref } from 'vue';
import { axiosClient } from '@/helpers/auth';
import { defineStore } from 'pinia';
import { useNotificationsStore } from '@/store/notificationsStore';
import { useGlobalStore } from '@/store/storeGlobal';

const debugPrefix = '[UploadV2]';

// Pure utility functions - moved to outer scope
function humanReadableFileSize (bytes)
{
  //const thresh = 1024 // 1024 for binary, 1000 for decimal
  const thresh = 1000;
  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }
  //Select the correct unit base on decimal (SI-units) or binary (KiB, MiB, ...)
  //const units = ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  const units = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let u = -1;
  do {
    bytes /= thresh;
    ++u;
  } while (Math.abs(bytes) >= thresh && u < units.length - 1);
  return bytes >= 100
    ? Math.round(bytes) + units[u]
    : bytes.toFixed(0).replace('.', ',') + units[u];
}

function dictionaryHasKey (dictionary, contentType)
{
  return contentType && Object.hasOwn(dictionary, contentType);
}

export const useUploadStoreV2 = defineStore('uploadStoreV2', () =>
{
  const notificationsStore = useNotificationsStore();
  const globalStore = useGlobalStore();

  function handleEventStreamUpdate (eventStreamUpdate)
  {
    if (!eventStreamUpdate?.fileId) return;

    const fileIndex = files.value.findIndex(f => f.uploadId === eventStreamUpdate.fileId);
    if (fileIndex === -1) {
      globalStore.debug(`${debugPrefix} EventStreamUpdate for unknown file: ${eventStreamUpdate.fileId}`);
      return;
    }

    const currentFile = files.value[fileIndex];
    files.value[fileIndex] = {
      ...currentFile,
      processType: eventStreamUpdate.processType || currentFile.processType,
      processStatus: eventStreamUpdate.processStatus || currentFile.processStatus,
      isComplete: eventStreamUpdate.isComplete ?? currentFile.isComplete,
      hasError: eventStreamUpdate.hasError ?? currentFile.hasError,
      progressPercent: eventStreamUpdate.progressPercent ?? currentFile.progressPercent,
      message: eventStreamUpdate.message || currentFile.message,
      currentMessage: eventStreamUpdate.message || currentFile.currentMessage,
      currentTaskName: eventStreamUpdate.taskName || currentFile.currentTaskName,
      timestamp: eventStreamUpdate.timestamp || currentFile.timestamp,
      counterData: eventStreamUpdate.counterData || currentFile.counterData
    };

    globalStore.debug(`${debugPrefix} Updated file ${eventStreamUpdate.fileId} from SignalR EventStreamUpdate`);
  }

  const colors = {
    'application/vnd.ms-excel': 'darkgreen',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'darkgreen',
    'text/csv': 'darkblue',
    'application/json': 'darkgray',
    'text/plain': 'darkgoldenrod',
    'text/xml': 'darkgray',
    'application/pdf': 'darkred',
    'application/zip': 'darkorange',
    'application/x-zip-compressed': 'darkorange',
    'application/gzip': 'darkpurple',
    'application/x-gzip': 'darkpurple',
    'application/gzip-compressed': 'darkpurple',
  };

  const icons = {
    'application/vnd.ms-excel': ['fal', 'file-excel'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['fal', 'file-excel'],
    'text/csv': ['fal', 'file-csv'],
    'application/json': ['fal', 'file-code'],
    'text/plain': ['fal', 'file-lines'],
    'text/xml': ['fal', 'file-code'],
    'application/pdf': ['fal', 'file-pdf'],
    'application/zip': ['fal', 'file-zipper'],
    'application/x-zip-compressed': ['fal', 'file-zipper'],
    'application/gzip': ['fal', 'file-zipper'],
    'application/x-gzip': ['fal', 'file-zipper'],
    'application/gzip-compressed': ['fal', 'file-zipper'],
  };


  const selectedFile = ref(null);
  const showArchive = ref(false);

  function toggleArchive ()
  {
    showArchive.value = !showArchive.value;
    globalStore.debug(`${debugPrefix} toggleArchive`, showArchive.value);
  }

  const itemsToShow = ref(10);

  const hasMoreItems = computed(() =>
  {
    return itemsToShow.value < (allFiles.value?.length || 0);
  });

  const itemsShowing = computed(() =>
  {
    return Math.min(itemsToShow.value, visibleFiles.value?.length || 0);
  });

  const selectedStatuses = ref([]);

  function toggleStatus (status)
  {
    const idx = selectedStatuses.value.indexOf(status);
    if (idx === -1) {
      selectedStatuses.value.push(status);
    } else {
      selectedStatuses.value.splice(idx, 1);
    }
  }

  function clearStatusFilter ()
  {
    selectedStatuses.value = [];
  }

  const visibleFiles = computed(() =>
  {
    let parents = showArchive.value
      ? filteredFilesTopLevel.value?.filter(file => file.isArchived)
      : filteredFilesTopLevel.value?.filter(file => !file.isArchived);
    parents = parents || [];

    if (selectedStatuses.value.length > 0) {
      parents = parents.filter(parent =>
      {
        if (selectedStatuses.value.includes(parent.processStatus)) return true;

        const children = filteredFiles.value.filter(f => f.extractedFromUploadId === parent.uploadId);
        return children.some(child => selectedStatuses.value.includes(child.processStatus));
      });
    }
    return parents.slice(0, itemsToShow.value);
  });

  const allFiles = computed(() =>
  {
    if (showArchive.value) {
      return filteredFilesTopLevel.value?.filter(file => file.isArchived) || [];
    }
    return filteredFilesTopLevel.value?.filter(file => !file.isArchived) || [];
  });

  function loadMore ()
  {
    itemsToShow.value += 10;
  }

  function selectFile (file)
  {
    if (selectedFile.value === file) {
      selectedFile.value = null;
      globalStore.debug(`${debugPrefix} selectFile`, file, 'deselected');
      return;
    }
    selectedFile.value = file;
    globalStore.debug(`${debugPrefix} selectFile`, file, 'selected');
  }

  function getIconFromContentType (file)
  {
    if (file.isProcessing) {
      return ['fal', 'spinner']
    }
    if (file.isExcelSheet) {
      return ['fal', 'table']
    }
    if (dictionaryHasKey(icons, file.contentType)) {
      return icons[file.contentType];
    }
    console.warn('getIconFromContentType', file.contentType, 'not found');
    return ['fal', 'file-circle-exclamation'];
  }

  function getColorFromContentType (file)
  {
    if (dictionaryHasKey(colors, file.contentType)) {
      return colors[file.contentType];
    }
    console.warn('getColorFromContentType', file.contentType, 'not found');
    return 'red';
  }

  const files = ref([]);
  const query = ref('');

  const searchMatches = computed(() =>
    query.value
      ? files.value.filter(f => f.fileName.toLowerCase().includes(query.value.toLowerCase()))
      : files.value,
  );

  const filteredFiles = computed(() =>
  {
    if (!query.value) return files.value;

    const matches = searchMatches.value;
    const parentIds = new Set(
      matches
        .filter(f => f.extractedFromUploadId)
        .map(f => f.extractedFromUploadId),
    );

    const childIds = new Set(
      matches
        .map(f => f.uploadId),
    );

    return files.value.filter(f =>
      matches.includes(f) || // Filen matcher søket
      parentIds.has(f.uploadId) || // Eller filen er forelder til en matchende fil
      childIds.has(f.extractedFromUploadId), // Eller filen er barn av en matchende fil
    );
  });

  const filteredFilesTopLevel = computed(() =>
    filteredFiles.value.filter(f => !f.extractedFromUploadId),
  );

  function clearQuery ()
  {
    query.value = '';
  }
  async function updateImportDefinitionId (uploadId, importDefinitionId)
  {
    globalStore.debug(`${debugPrefix} updateImportDefinitionId`, uploadId, importDefinitionId);
    const params = {
      uploadId: uploadId,
      importDefinitionId: importDefinitionId,
    };
    await axiosClient.put('v2/upload/import-definition-id', null, { params });

    await getFiles();
  }

  // Oppdater fil-metadata - dette er uavhengig av import-definisjon
  async function updateMetadata (uploadId, metadata)
  {
    if (!uploadId) {
      notificationsStore.addError('Kan ikke lagre til ukjent fil-entitet');
      return;
    }
    if (!metadata) {
      notificationsStore.addError('Kan ikke lagre tomt skjema');
      return;
    }

    globalStore.debug(`${debugPrefix} update`, uploadId, metadata);
    await axiosClient.put(`v2/upload/file-metadata`, metadata, { params: { uploadId } });
  }
  function updateOrAddFile (newFile)
  {
    if (!newFile?.uploadId) {
      console.warn(`${debugPrefix} updateOrAddFile(): Missing uploadId`, newFile);
      return;
    }

    const index = files.value.findIndex(f => f.uploadId === newFile.uploadId);

    if (index !== -1) {
      // Oppdater eksisterende fil
      files.value[index] = { ...files.value[index], ...newFile };
      globalStore.debug(`${debugPrefix} updateOrAddFile(): Updated file`, newFile);
      return;
    }
    // Legg til ny fil
    files.value.unshift(newFile);
    globalStore.debug(`${debugPrefix} updateOrAddFile(): Added new file`, newFile);
  }

  const loading = ref(false);

  async function getFiles ()
  {
    loading.value = true;
    showArchive.value = false;
    const response = await axiosClient.get('v2/upload/files');
    if (response?.data) {
      globalStore.debug(`${debugPrefix} getFiles()`, response.data);
      files.value = response.data?.items;
    }
    loading.value = false;
  }

  function close ()
  {
    // Reset state when dialog is closed
    showArchive.value = false;
    query.value = '';
    globalStore.debug(`${debugPrefix} close()`, 'Dialog closed, state reset');
  }

  async function downloadFile ({ uploadId, fileName })
  {
    const response = await axiosClient.get('v2/upload/file', { responseType: 'blob', params: { uploadId } });
    if (response?.data) {
      const url = globalThis.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      notificationsStore.addSuccess(`Lastet ned ${fileName}`);

    }
  }

  async function deleteFile ({ uploadId, fileName })
  {
    const response = await axiosClient.delete('v2/upload/file', { params: { uploadId } });
    await getFiles();
    if (response) {
      notificationsStore.addSuccess(`Slettet ${fileName}`);
    }
  }

  async function archiveFile ({ uploadId, fileName })
  {
    const response = await axiosClient.put(`v2/upload/file/${uploadId}/archive`);
    await getFiles();
    if (response) {
      notificationsStore.addSuccess(`Arkiverte ${fileName}`);
    }
  }

  async function retryFile (uploadId)
  {
    globalStore.debug(`${debugPrefix} retryFile`, uploadId);
    const response = await axiosClient.put(`v2/upload/file/${uploadId}/retry`);
    await getFiles();
    if (response) {
      notificationsStore.addSuccess('Filen vil bli behandlet på nytt');
    }
  }

  async function postFiles (files, progressUpdate)
  {
    const formData = new FormData();
    for (const file of files) {
      globalStore.debug(`${debugPrefix} files`, file, file.name);
      formData.append('files', file, file.name);
    }
    // noinspection JSUnusedGlobalSymbols
    const response = await axiosClient.post('v2/upload', formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress (progressEvent)
        {
          progressUpdate(
            Number.parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100),
            ),
          );
        },
      },
    );
    globalStore.debug(`${debugPrefix} SUCCESS!!`);
    // Hent filer på nytt for å oppdatere status
    await getFiles();
    return response?.data; // Returner array av uploadId
  }

  // Getters
  function getFileByUploadId (uploadId)
  {
    return files.value.find(f => f.uploadId === uploadId) || null;
  }

  function getFilesByExtractedFromUploadId (parentUploadId)
  {
    return files.value.filter(f => f.extractedFromUploadId === parentUploadId);
  }

  async function fetchFileStatus (uploadId)
  {
    if (!uploadId) return;
    try {
      const response = await axiosClient.get(`v2/upload/file/${uploadId}/status`);
      if (response?.data) {
        updateOrAddFile(response.data);
      }
    } catch (error) {
      notificationsStore.addError('Kunne ikke hente filstatus', error?.message || error);
    }
  }


  return {
    loading,
    files,
    postFiles,
    getFiles,
    handleEventStreamUpdate,
    downloadFile,
    deleteFile,
    archiveFile,
    retryFile,
    getIconFromContentType,
    getColorFromContentType,
    humanReadableFileSize,
    updateImportDefinitionId,
    updateMetadata,
    toggleArchive,
    query,
    filteredFiles,
    filteredFilesTopLevel,
    clearQuery,
    updateOrAddFile,
    selectedFile,
    selectFile,
    showArchive,

    hasMoreItems, itemsShowing, loadMore, allFiles, visibleFiles,
    close,
    selectedStatuses,
    toggleStatus,
    clearStatusFilter,
    getFileByUploadId,
    getFilesByExtractedFromUploadId,
    fetchFileStatus
  };
})
  ;

